import { httpError } from '../utils/userError';
import { getAuthToken } from '../utils/authToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

/**
 * Client for the /api/workspace surface.
 *
 * Since #147 ("Soulfile") this is effectively the RULEBOOK client: the SOUL
 * editor was removed from the UI, so SOUL is an internal personalization store
 * the backend maintains on its own (the username rewrite writes it; the
 * system-instruction builder reads it). Since #399 USER no longer exists (merged
 * into SOUL) — the API accepts SOUL and RULEBOOK, and a SOUL PUT over 4,000
 * characters is a 400 `too_large`.
 *
 * getFile/updateFile stay because RulebookPanel reads and writes 'RULEBOOK'
 * through them.
 */
export const workspaceService = {
    /**
     * Get a specific workspace file
     * @param {string} fileType - File type (SOUL, RULEBOOK)
     * @returns {Promise<Object>} Workspace file object
     */
    async getFile(fileType) {
        const token = getAuthToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await fetch(`${API_BASE_URL}/api/workspace/files/${fileType}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: { message: 'Failed to fetch workspace file' }}));
            throw httpError(response.status, errorData);
        }

        return response.json();
    },

    /**
     * Update a workspace file's content
     * @param {string} fileType - File type (SOUL, RULEBOOK)
     * @param {string} content - New content for the file
     * @returns {Promise<Object>} Updated workspace file object
     */
    async updateFile(fileType, content) {
        const token = getAuthToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await fetch(`${API_BASE_URL}/api/workspace/files/${fileType}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ content })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: { message: 'Failed to update workspace file' }}));
            throw httpError(response.status, errorData);
        }

        return response.json();
    },


    /**
     * The Assistant Desk conversation (PAI Rulebook #146).
     *
     * Find-or-creates THE user's single persistent desk conversation, seeded
     * with one welcome message from PAI, and returns it with its thread.
     * Idempotent — safe to call on every panel open.
     *
     * @returns {Promise<{conversation_id: string, title: string, created: boolean, messages: Array}>}
     */
    async getRulebookDesk() {
        const token = getAuthToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await fetch(`${API_BASE_URL}/api/workspace/rulebook/desk`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: { message: 'Failed to fetch the Assistant Desk' }}));
            throw httpError(response.status, errorData);
        }

        return response.json();
    },

    /**
     * Run ONE Assistant Desk turn with the scribe and stream the reply back
     * (PAI Rulebook #146, Part B).
     *
     * A small dedicated SSE reader: `documentService.ragQueryStream` is a
     * ~770-line parser hardcoded to the RAG endpoint (its URL, its request
     * shape and ~40 event branches), with nothing reusable exported — so this
     * reads the same `data: ` wire format for the two events the Desk actually
     * has: content tokens and the `rule_add_request` card.
     *
     * @param {string} message - the composed turn (typed text and/or the
     *   `[Typed]` / `[Voice N]` labelled hybrid content the shared
     *   `composeSnapshot()` builds — identical to what the Main Chat sends)
     * @param {{metadata?: Object, onToken?: Function, onCard?: Function, onDone?: Function, onError?: Function, signal?: AbortSignal}} handlers
     *   `metadata` is the composer payload (attached_documents / voice_segments /
     *   typed_text). The backend whitelists it and persists it on the user's
     *   message, so attachment chips and voice bubbles survive a reload; the
     *   scribe additionally reads the attached documents' text (Rulebook messages).
     * @returns {Promise<{content: string, card: Object|null, messageId: string|null}>}
     */
    async streamRulebookDesk(message, { metadata, onToken, onCard, onDone, onError, signal } = {}) {
        const token = getAuthToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await fetch(`${API_BASE_URL}/api/workspace/rulebook/desk-stream`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(metadata ? { message, metadata } : { message }),
            signal,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw httpError(response.status, errorData);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let content = '';
        let card = null;
        let messageId = null;

        const finish = () => {
            const result = { content, card, messageId };
            if (onDone) onDone(result);
            return result;
        };

        try {
            /* eslint-disable no-constant-condition */
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // keep the incomplete tail

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    const data = line.slice(6);
                    if (data === '[DONE]') {
                        try { await reader.cancel(); } catch { /* already closed */ }
                        return finish();
                    }
                    let parsed;
                    try {
                        parsed = JSON.parse(data);
                    } catch {
                        continue; // one bad frame must not kill the stream
                    }

                    if (parsed.type === 'rule_add_request') {
                        card = parsed;
                        if (onCard) onCard(parsed);
                        continue;
                    }
                    if (parsed.message_id) messageId = parsed.message_id;
                    const delta = parsed.choices?.[0]?.delta;
                    if (delta?.content) {
                        content += delta.content;
                        if (onToken) onToken(delta.content, content);
                    }
                }
            }
        } catch (err) {
            if (onError) onError(err);
            throw err;
        } finally {
            try { await reader.cancel(); } catch { /* already closed */ }
        }

        return finish();
    },

    /**
     * Append an accepted draft to the Folio as the next numbered rule
     * (PAI Rulebook #146 — the Add Rule button). The SERVER owns the numbering.
     *
     * @param {string} ruleText
     * @returns {Promise<{rule_number: number, rule_text: string, content: string, version: number}>}
     */
    async appendRule(ruleText) {
        const token = getAuthToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await fetch(`${API_BASE_URL}/api/workspace/rulebook/append-rule`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ rule_text: ruleText }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: { message: 'Failed to add the rule' } }));
            throw httpError(response.status, errorData);
        }

        return response.json();
    },

    /**
     * One rule-level change to the Folio (Rulebook in Telegram #503) — the web
     * Confirm of the `rule_change_request` card PAI shows for edit_rulebook.
     * `action` is add | update | delete; `expectedText` is the rule text the card
     * showed, so the server refuses (409 `stale`) a Folio that moved underneath it
     * instead of changing a different rule. The SERVER owns numbering.
     *
     * @param {{action: 'add'|'update'|'delete', ruleNumber?: number, ruleText?: string, expectedText?: string}} change
     * @returns {Promise<{action: string, rule_number: number, new_text: ?string, current_text: ?string, content: string, rules: Array, event: object}>}
     */
    async changeRule({ action, ruleNumber, ruleText, expectedText }) {
        const token = getAuthToken();
        if (!token) {
            throw new Error('No authentication token found');
        }
        const base = `${API_BASE_URL}/api/workspace/rulebook/rules`;
        const url = action === 'add' ? base : `${base}/${encodeURIComponent(ruleNumber)}`;
        const method = { add: 'POST', update: 'PATCH', delete: 'DELETE' }[action];
        if (!method) throw new Error(`Unknown rule action: ${action}`);
        const body = {};
        if (action !== 'delete') body.rule_text = ruleText;
        if (action !== 'add' && expectedText != null) body.expected_text = expectedText;

        const response = await fetch(url, {
            method,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: { message: 'Failed to change the rule' } }));
            throw httpError(response.status, errorData);
        }

        return response.json();
    }
};
